import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  RadioGroup,
  EmptyState,
  OutstandingLoansSection,
  EmployeeSearchInput,
  useConfirm,
} from "./shared/components";
import {
  getMyResignations,
  getResignationDetails,
  submitNoDues,
  normalizeSeparationKind,
  formatDate,
  EmployeeSearchResult,
} from "./services/eSeparation.service";

const SEPARATION_MODES = [
  "Resignation",
  "Superannuation",
  "Retirement",
  "Termination",
  "Death",
  "Deputation",
  "Transfer-Out",
];

// Case types that follow ONLY the No-Dues workflow (no resignation / CA approval).
const NO_DUES_ONLY_TYPES = [
  "TERMINATION",
  "DEATH",
  "DEPUTATION",
  "RETIREMENT",
  "TRANSFER_OUT",
];
const isNoDuesOnlyRecord = (r: any): boolean =>
  NO_DUES_ONLY_TYPES.includes(String(r?.request_type ?? "").toUpperCase()) &&
  String(r?.workflow_version ?? "").toUpperCase() === "V2";

// Fixed advance types the No-Dues surety table asks for — matches the
// physical form's "Advances availed from DMRC" table exactly (4 rows, 2
// surety slots each). Not dynamic/addable, unlike the resignation form's
// open-ended loan_sureties list.
const NO_DUES_ADVANCE_TYPES: { code: string; label: string }[] = [
  { code: "HBA", label: "HBA Advance" },
  { code: "VEHICLE", label: "Vehicle Advance" },
  { code: "MULTIPURPOSE", label: "Multipurpose Advance" },
  { code: "DMRC_CREDIT_SOCIETY", label: "DMRC Credit Society" },
];

interface AdvanceSuretyRow {
  advance_type: string;
  first_surety_emp_id: string;
  first_surety_emp_name?: string;
  second_surety_emp_id: string;
  second_surety_emp_name?: string;
}

// Wing is a dropdown on the certificate — O&M or Project.
const WING_OPTIONS = ["O&M", "Project"];

// Normalise the stored SAP wing code (e.g. "PROJ", "O&M") to a dropdown value.
const matchWing = (stored: any): string => {
  const s = String(stored ?? "").trim().toUpperCase();
  if (!s) return "";
  if (s.startsWith("PROJ")) return "Project";
  if (s.startsWith("O&M") || s === "OM" || s.startsWith("O AND M")) return "O&M";
  return WING_OPTIONS.find((w) => w.toUpperCase() === s) || "";
};

// The certificate needs the reviewing authority (Dy. HOD / HOD / Director).
// Pick every populated level so nothing from the record is dropped.
const reviewingAuthorities = (r: any) => {
  const rows: { role: string; name: any; emp_no: any; designation: any }[] = [];
  if (r?.dy_hod_name || r?.dy_hod_emp_no) {
    rows.push({
      role: "Dy. HOD",
      name: r.dy_hod_name,
      emp_no: r.dy_hod_emp_no,
      designation: r.dy_hod_designation,
    });
  }
  if (r?.hod_name || r?.hod_emp_no) {
    rows.push({
      role: "HOD",
      name: r.hod_name,
      emp_no: r.hod_emp_no,
      designation: r.hod_designation,
    });
  }
  if (r?.director_name || r?.director_emp_no) {
    rows.push({
      role: "Director",
      name: r.director_name,
      emp_no: r.director_emp_no,
      designation: r.director_designation,
    });
  }
  return rows;
};

const fmt = (v: any): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    return formatDate(v);
  }
  return String(v);
};

// Map stored separation_type to one of the form's display labels.
const matchSeparationMode = (stored: any): string => {
  const s = String(stored ?? "").trim().toLowerCase().replace(/_/g, "-");
  return SEPARATION_MODES.find((m) => m.toLowerCase() === s) || "Resignation";
};

// Notice period is a fixed 90 days; shortfall_days tells how much was served.
const noticeServed = (r: any): string => {
  const shortfall = Number(r?.shortfall_days) || 0;
  if (r?.early_relieve === "Y" && shortfall > 0) {
    return `${Math.max(0, 90 - shortfall)} of 90 days (${shortfall} days short)`;
  }
  return "90 days (full notice)";
};

const NoDuesForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  // HR fills this form on the employee's behalf (HR-initiated No-Dues-only case:
  // there is no employee-side form). Submitting sends it straight to the
  // departments — no approval step.
  const hrFill = !!(location.state as any)?.hrFill;

  const stateRequestId = (location.state as any)?.request_id as
    | string
    | undefined;

  const [requestId, setRequestId] = useState<string>(stateRequestId || "");
  const [loading, setLoading] = useState(true);
  const [resignation, setResignation] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  // Employee-filled fields
  const [mobile, setMobile] = useState("");
  const [wing, setWing] = useState("");
  const [separationMode, setSeparationMode] = useState("Resignation");
  const [noticePeriodServed, setNoticePeriodServed] = useState("");
  const [bondAmount, setBondAmount] = useState("");

  const [leaveCurrentMonth, setLeaveCurrentMonth] = useState("");
  const [trainingProvided, setTrainingProvided] = useState("N");
  const [trainingDetails, setTrainingDetails] = useState("");
  const [otherBondSubmitted, setOtherBondSubmitted] = useState("N");
  const [otherBondDetails, setOtherBondDetails] = useState("");
  const [advancesAvailed, setAdvancesAvailed] = useState("");
  const [advanceSureties, setAdvanceSureties] = useState<AdvanceSuretyRow[]>(
    NO_DUES_ADVANCE_TYPES.map((t) => ({
      advance_type: t.code,
      first_surety_emp_id: "",
      second_surety_emp_id: "",
    }))
  );
  const [declaration, setDeclaration] = useState(false);

  const prefilledRef = useRef(false);

  // Prefill carry-over fields from the resignation exactly once.
  const prefill = (data: any) => {
    setResignation(data);
    setMobile((prev) => prev || data.mobile || "");
    if (prefilledRef.current) return;
    prefilledRef.current = true;
    setWing(matchWing(data.wing));
    setSeparationMode(matchSeparationMode(data.separation_type));
    // No notice period applies to a No-Dues-only case (no resignation).
    setNoticePeriodServed(isNoDuesOnlyRecord(data) ? "N/A" : noticeServed(data));
    if (
      data.training_bond_amount != null &&
      Number(data.training_bond_amount) > 0
    ) {
      setBondAmount(String(data.training_bond_amount));
    }

  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      let reqId = stateRequestId || "";
      if (!reqId) {
        const list = await getMyResignations();
        const active = (list.data || []).find(
          (r) =>
            (normalizeSeparationKind(r.type) === "RESIGNATION" ||
              NO_DUES_ONLY_TYPES.includes(String(r.raw?.request_type ?? "").toUpperCase())) &&
            ["REACHED_HR", "CA_APPROVED", "ON_HOLD", "DEPT_CLEARANCE", "PENDING_APPROVAL", "APPROVED"].includes(
              String(r.status ?? "").toUpperCase()
            )
        );
        if (active) reqId = active.id;
      }
      if (!mounted) return;
      setRequestId(reqId);
      if (reqId) {
        const det = await getResignationDetails(reqId);
        if (!mounted) return;
        if (det.data) prefill(det.data);
        else toast.error("Failed to load request details");
      }
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRequestId]);

  const handleSubmit = async () => {
    if (!requestId) return toast.error("Missing request reference");
    if (!mobile.trim()) return toast.error("Mobile number is required");
    if (!hrFill && !declaration)
      return toast.error("Please accept the declaration to proceed");

    const ok = await confirm(
      hrFill
        ? "Once submitted, this No-Dues is sent straight to all clearing departments — there is no approval step. Continue?"
        : "Once submitted, the No-Dues form follows the approval hierarchy up to HR. Continue?",
      { title: "Submit No-Dues Form", confirmLabel: "Submit" }
    );
    if (!ok) return;

    const authorities = reviewingAuthorities(resignation);
    const details = {
      mobile: mobile.trim(),
      wing,
      separation_mode: separationMode,
      notice_period_served: noticePeriodServed.trim(),
      bond_amount: bondAmount.trim(),

      leave_current_month: leaveCurrentMonth.trim(),
      training_provided: trainingProvided,
      training_details: trainingProvided === "Y" ? trainingDetails.trim() : "",
      other_bond_submitted: otherBondSubmitted,
      other_bond_details: otherBondSubmitted === "Y" ? otherBondDetails.trim() : "",
      controlling_officer: {
        name: resignation?.co_name || "",
        emp_no: resignation?.co_emp_no || "",
        designation: resignation?.co_designation || "",
      },
      reviewing_authorities: authorities,
      outstanding_loans: resignation?.outstanding_loans || [],
      advances_availed: advancesAvailed.trim(),
      loan_surety_flag: resignation?.surety_flag || "N",
      loan_surety_details: resignation?.surety_details || "",
      advance_sureties: advanceSureties.map((r) => ({
        advance_type: r.advance_type,
        first_surety_emp_id: r.first_surety_emp_id.trim() || "N/A",
        first_surety_emp_name: r.first_surety_emp_id.trim() ? r.first_surety_emp_name || "" : "N/A",
        second_surety_emp_id: r.second_surety_emp_id.trim() || "N/A",
        second_surety_emp_name: r.second_surety_emp_id.trim() ? r.second_surety_emp_name || "" : "N/A",
      })),
      declaration_accepted: hrFill ? false : declaration,
      filed_by_hr: hrFill,
    };

    setSubmitting(true);
    const res = await submitNoDues({ request_id: requestId, details });
    setSubmitting(false);
    if (res.success) {
      toast.success(
        hrFill
          ? "No-Dues submitted and sent to the departments for clearance"
          : "No-Dues form submitted successfully"
      );
      if (hrFill) navigate(routes.Esepration.hrInitiatedCases);
      else navigate(routes.Esepration.overview, { replace: true });
    } else {
      toast.error(res.message || "Failed to submit No-Dues form");
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
            <p className="text-muted mt-3 mb-0">Loading Request Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!requestId) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h4 className="fw-bold text-dark mb-0">Submit No-Dues Form</h4>
              <span className="text-muted small">
                Fill &amp; submit your No-Dues form once your resignation is approved by the Competent Authority (CA)
              </span>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
          <EmptyState
            icon="ti-file-off"
            title="No-Dues not available yet"
            message="Your No-Dues form becomes available once your resignation is approved by the Competent Authority (CA)."
            action={
              <button
                className="btn btn-primary"
                onClick={() => navigate(routes.Esepration.overview)}
              >
                Go to Overview
              </button>
            }
          />
        </div>
      </div>
    );
  }

  // A No-Dues-only case has no Competent Authority — its form is open from the
  // moment the case exists.
  const isCAApproved =
    resignation &&
    (isNoDuesOnlyRecord(resignation) ||
      ["CA_APPROVED", "ON_HOLD", "DEPT_CLEARANCE", "APPROVED"].includes(
        String(resignation.status ?? "").toUpperCase()
      ));

  if (resignation && !isCAApproved) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h4 className="fw-bold text-dark mb-0">Submit No-Dues Form</h4>
              <span className="text-muted small">
                Fill &amp; submit your No-Dues form once your resignation is approved by the Competent Authority (CA)
              </span>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
          <EmptyState
            icon="ti-lock"
            title="No-Dues not available yet"
            message={`Your resignation status is currently "${resignation.status}". No-Dues form can only be initiated after your resignation has been approved by the Competent Authority (CA).`}
            action={
              <button
                className="btn btn-primary"
                onClick={() => navigate(routes.Esepration.overview)}
              >
                Go to Overview
              </button>
            }
          />
        </div>
      </div>
    );
  }

  const revAuthorities = reviewingAuthorities(resignation);

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* Breadcrumbs & Page Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 small">
                <li className="breadcrumb-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(routes.Esepration.hub);
                    }}
                    className="text-decoration-none text-muted"
                  >
                    E-Separation
                  </a>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  No-Dues Form
                </li>
              </ol>
            </nav>
            <h4 className="fw-bold text-dark mb-0">No-Dues Form</h4>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
              <i className="ti ti-arrow-left me-1" />
              Exit
            </button>
          </div>
        </div>

        {/* Information Banner */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body py-3 px-4">
            <div className="d-flex align-items-center gap-3">
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, borderRadius: 10, background: "#e8f4fd", color: "#0d6efd" }}
              >
                <i className="ti ti-file-text" style={{ fontSize: 22 }} />
              </span>
              <div>
                <div
                  style={{
                    fontSize: "17px",
                    fontWeight: 700,
                    color: "#344054",
                    lineHeight: "1.3",
                  }}
                >
                  No-Dues Form
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#667085",
                    lineHeight: "1.4",
                    marginTop: "2px",
                  }}
                >
                  {hrFill
                    ? "You are filling this form on the employee's behalf. Fields marked Auto are pulled from their records; complete the remaining fields."
                    : "Fields marked Auto are pulled from your records. Complete the remaining fields and accept the declaration."}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 01: EMPLOYEE DETAILS (AUTO-FILLED) */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                01
              </span>
              Employee Details (Auto-filled)
            </h5>
          </div>
          <div className="card-body p-4">
            <div
              className="alert alert-info border-0 d-flex align-items-center gap-2 mb-4 py-2 px-3 rounded-3"
              style={{ background: "#e8f4fd", color: "#0c5460" }}
            >
              <i className="ti ti-info-circle fs-5 flex-shrink-0" />
              <span className="small">
                These details are auto-filled from SAP HR Master Data.
              </span>
            </div>

            <div className="row g-3">
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.emp_name)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Employee No.</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.emp_no)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={fmt(resignation?.designation_text || resignation?.designation)}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Wing</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.wing)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Department</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={fmt(resignation?.department_name || resignation?.posting)}
                  readOnly
                />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Pay Scale</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.pay_scale)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Basic Pay</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.basic_pay)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Date of Joining</label>
                <input type="text" className="form-control bg-light" value={fmt(resignation?.doj)} readOnly />
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Employee Group</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={
                    resignation?.emp_group === "A"
                      ? "Executive"
                      : resignation?.emp_group === "B"
                      ? "Non-Executive"
                      : fmt(resignation?.emp_group)
                  }
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 02: CONTROLLING & REVIEWING OFFICERS */}
        {((resignation?.co_name || resignation?.co_emp_no) || revAuthorities.length > 0) && (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
              <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                  style={{ width: 34, height: 34, fontSize: 15 }}
                >
                  02
                </span>
                Controlling &amp; Reviewing Officers
              </h5>
            </div>
            <div className="card-body p-4">
              {/* Controlling Officer Sub-section */}
              {(resignation?.co_name || resignation?.co_emp_no) && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">Controlling Officer Details</h6>
                  <div className="row g-3">
                    <div className="col-lg-4 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
                      <input type="text" className="form-control bg-light" value={fmt(resignation?.co_name)} readOnly />
                    </div>
                    {resignation?.co_emp_no && (
                      <div className="col-lg-4 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">Employee No.</label>
                        <input type="text" className="form-control bg-light" value={fmt(resignation?.co_emp_no)} readOnly />
                      </div>
                    )}
                    {resignation?.co_designation && (
                      <div className="col-lg-4 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
                        <input type="text" className="form-control bg-light" value={fmt(resignation?.co_designation)} readOnly />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reviewing Officers Sub-section */}
              {revAuthorities.length > 0 && (
                <div>
                  <h6 className="fw-bold text-primary mb-1">Reviewing / Reporting Officer Details</h6>
                  <p className="text-muted small mb-3">
                    For Non-Exe → Dy. HOD, for AM up to Dy. HOD → HOD, for HOD → Director.
                  </p>
                  <div className="d-flex flex-column gap-3">
                    {revAuthorities.map((a) => (
                      <div key={a.role} className="p-3 border rounded-3 bg-light">
                        <div className="badge bg-secondary mb-2 px-2 py-1">{a.role}</div>
                        <div className="row g-3">
                          <div className="col-lg-4 col-md-4 col-sm-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Name</label>
                            <input type="text" className="form-control bg-white" value={fmt(a.name)} readOnly />
                          </div>
                          <div className="col-lg-4 col-md-4 col-sm-6">
                            <label className="form-label fw-semibold text-secondary small mb-1">Employee No.</label>
                            <input type="text" className="form-control bg-white" value={fmt(a.emp_no)} readOnly />
                          </div>
                          {a.designation && (
                            <div className="col-lg-4 col-md-4 col-sm-6">
                              <label className="form-label fw-semibold text-secondary small mb-1">Designation</label>
                              <input type="text" className="form-control bg-white" value={fmt(a.designation)} readOnly />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTION 03: SEPARATION REFERENCE (AUTO-FILLED) */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                03
              </span>
              Separation Reference (Auto-filled)
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="row g-3">
              <div className="col-lg-4 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Request ID</label>
                <input type="text" className="form-control bg-light" value={fmt(requestId)} readOnly />
              </div>

              <div className="col-lg-4 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Date of Leaving</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={fmt(
                    resignation?.notice_end_date ||
                      resignation?.date_of_leaving ||
                      resignation?.approved_wef ||
                      resignation?.desired_relieve_date
                  )}
                  readOnly
                />
              </div>

              <div className="col-lg-4 col-md-4 col-sm-6">
                <label className="form-label fw-semibold text-secondary small mb-1">Date of Submission</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={fmt(resignation?.intimation_date)}
                  readOnly
                />
              </div>

              <div className="col-12 mt-2">
                <label className="form-label fw-semibold text-secondary small mb-1">Reason for Leaving</label>
                <div
                  className="p-3 rounded-3 bg-light text-dark small"
                  style={{ border: "1px solid #e9ecef", minHeight: 60, whiteSpace: "pre-wrap" }}
                >
                  {fmt(resignation?.reason)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 04: DETAILS TO BE FILLED BY EMPLOYEE */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                04
              </span>
              Details to be Filled by Employee
            </h5>
          </div>
          <div className="card-body p-4">
            <p className="text-muted small mb-4">
              Mode of separation, notice period, bond amount and bond details are prefilled from your resignation — edit if needed.
            </p>

            <div className="row g-3">
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label fw-semibold text-secondary small mb-1">Wing</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={wing}
                  readOnly
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Notice Period Served (if applicable)
                </label>
                <input
                  className="form-control"
                  placeholder="e.g. 90 days"
                  value={noticePeriodServed}
                  onChange={(e) => setNoticePeriodServed(e.target.value)}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Bond Amount, if any (if applicable)
                </label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="e.g. 0"
                  value={bondAmount}
                  onChange={(e) => setBondAmount(e.target.value)}
                />
              </div>

              <div className="col-lg-8 col-md-6 col-sm-12">
                <label className="form-label fw-semibold text-secondary small mb-1">
                  Leave for current month received from department
                </label>
                <input
                  className="form-control"
                  placeholder="e.g. CL/EL details"
                  value={leaveCurrentMonth}
                  onChange={(e) => setLeaveCurrentMonth(e.target.value)}
                />
              </div>

              <div className="col-12 mt-3">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Whether any training provided by DMRC (including Induction Course or nay other
                  course) (in India/Abroad)
                </label>
                <RadioGroup
                  value={trainingProvided === "Y" ? "yes" : "no"}
                  onChange={(v) => setTrainingProvided(v === "yes" ? "Y" : "N")}
                />
              </div>
              {trainingProvided === "Y" && (
                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Training Details
                  </label>
                  <input
                    className="form-control"
                    placeholder="Course name, location, duration…"
                    value={trainingDetails}
                    onChange={(e) => setTrainingDetails(e.target.value)}
                  />
                </div>
              )}

              <div className="col-12 mt-3">
                <label className="form-label fw-semibold text-secondary small mb-2">
                  Whether any other Bond submitted to DMRC other than the Joining Bond
                </label>
                <RadioGroup
                  value={otherBondSubmitted === "Y" ? "yes" : "no"}
                  onChange={(v) => setOtherBondSubmitted(v === "yes" ? "Y" : "N")}
                />
              </div>
              {otherBondSubmitted === "Y" && (
                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small mb-1">
                    Other Bond Details
                  </label>
                  <input
                    className="form-control"
                    placeholder="Describe the bond…"
                    value={otherBondDetails}
                    onChange={(e) => setOtherBondDetails(e.target.value)}
                  />
                </div>
              )}

              <div className="col-12 mt-3">
                <label className="form-label fw-semibold text-secondary small mb-1">Mode of Separation</label>
                <div className="d-flex flex-wrap gap-2">
                  {SEPARATION_MODES.map((m) => {
                    const active = separationMode === m;
                    const isDisabled = m !== "Resignation";
                    return (
                      <button
                        key={m}
                        type="button"
                        className="btn btn-sm px-3 py-2"
                        disabled={isDisabled}
                        style={{
                          borderRadius: 8,
                          border: active
                            ? "1px solid #185FA5"
                            : "1px solid rgba(0,0,0,0.12)",
                          background: active ? "#185FA5" : "#f8f9fa",
                          color: active ? "#fff" : "#adb5bd",
                          fontWeight: active ? 600 : 400,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.65 : 1,
                        }}
                        onClick={() => !isDisabled && setSeparationMode(m)}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* SECTION 05: OUTSTANDING LOANS / ADVANCES */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                05
              </span>
              Outstanding Loans / Advances
            </h5>
          </div>
          <div className="card-body p-4">
            <OutstandingLoansSection
              loans={resignation?.outstanding_loans || []}
              loading={loading}
              note="Auto-fetched from your SAP loan records — these will be carried into your No-Dues."
            />

            <div className="mt-4 pt-3 border-top">
              <label className="form-label fw-semibold text-secondary small mb-1">
                Advances availed from DMRC (HBA/Vehicle/Multipurpose/Laptop/DMRC Credit Society)
              </label>
              <input
                className="form-control"
                placeholder="e.g. No, or HBA Advance"
                value={advancesAvailed}
                onChange={(e) => setAdvancesAvailed(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* SECTION 06: SURETY & DECLARATION */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                style={{ width: 34, height: 34, fontSize: 15 }}
              >
                06
              </span>
              Surety &amp; Declaration
            </h5>
          </div>
          <div className="card-body p-4">
            <div className="mb-4">
              <label className="form-label fw-semibold text-secondary small mb-1">
                Your Sureties for Loans (Auto-fetched)
              </label>
              <input
                type="text"
                className="form-control bg-light mb-1"
                value={
                  resignation?.surety_flag === "Y"
                    ? resignation?.surety_details || "Yes"
                    : "No sureties on record"
                }
                readOnly
              />
              <small className="text-muted d-block">
                Auto-fetched from your SAP loan records — cannot be edited.
              </small>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold text-secondary small mb-2">
                Advances availed from DMRC — Name &amp; Emp. No. of employees for whom Surety is
                given
              </label>
              <small className="text-muted d-block mb-2">
                Leave blank for any advance type/slot with no surety — it will be recorded as N/A.
              </small>
              {/* Div-based "table" rather than a real <table> — a real
                  table's per-cell stacking context clips
                  EmployeeSearchInput's absolutely-positioned results
                  dropdown behind the next row, exactly like the resignation
                  form's own (working) loan-sureties list already avoids by
                  not using <table>. */}
              <div className="border rounded-3">
                <div className="row g-0 bg-light border-bottom fw-semibold small text-secondary px-3 py-2">
                  <div className="col-1">S.No.</div>
                  <div className="col-3">Type of Advance</div>
                  <div className="col-4">Surety 1 — Name, E.No.</div>
                  <div className="col-4">Surety 2 — Name, E.No.</div>
                </div>
                {NO_DUES_ADVANCE_TYPES.map((t, i) => {
                  const row = advanceSureties[i];
                  return (
                    <div
                      key={t.code}
                      className={`row g-2 align-items-start px-3 py-3${
                        i < NO_DUES_ADVANCE_TYPES.length - 1 ? " border-bottom" : ""
                      }`}
                    >
                      <div className="col-1 pt-2">{i + 1}</div>
                      <div className="col-3 fw-semibold pt-2">{t.label}</div>
                      <div className="col-4">
                        <EmployeeSearchInput
                          value={row.first_surety_emp_id}
                          placeholder="Type employee number or name…"
                          onChange={(v) =>
                            setAdvanceSureties((prev) =>
                              prev.map((r, idx) =>
                                idx === i
                                  ? { ...r, first_surety_emp_id: v, first_surety_emp_name: undefined }
                                  : r
                              )
                            )
                          }
                          onSelect={(emp: EmployeeSearchResult) =>
                            setAdvanceSureties((prev) =>
                              prev.map((r, idx) =>
                                idx === i
                                  ? {
                                      ...r,
                                      first_surety_emp_id: emp.emp_no,
                                      first_surety_emp_name: emp.emp_name,
                                    }
                                  : r
                              )
                            )
                          }
                        />
                        {row.first_surety_emp_name && (
                          <small className="text-muted d-block mt-1">
                            {row.first_surety_emp_name}
                          </small>
                        )}
                      </div>
                      <div className="col-4">
                        <EmployeeSearchInput
                          value={row.second_surety_emp_id}
                          placeholder="Type employee number or name…"
                          onChange={(v) =>
                            setAdvanceSureties((prev) =>
                              prev.map((r, idx) =>
                                idx === i
                                  ? { ...r, second_surety_emp_id: v, second_surety_emp_name: undefined }
                                  : r
                              )
                            )
                          }
                          onSelect={(emp: EmployeeSearchResult) =>
                            setAdvanceSureties((prev) =>
                              prev.map((r, idx) =>
                                idx === i
                                  ? {
                                      ...r,
                                      second_surety_emp_id: emp.emp_no,
                                      second_surety_emp_name: emp.emp_name,
                                    }
                                  : r
                              )
                            )
                          }
                        />
                        {row.second_surety_emp_name && (
                          <small className="text-muted d-block mt-1">
                            {row.second_surety_emp_name}
                          </small>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {hrFill ? (
              <div className="p-3 rounded-3 border bg-light small text-muted">
                Filed by HR on behalf of the employee — no employee declaration is
                required for this case.
              </div>
            ) : (
            <div
              className="p-3 rounded-3 border"
              style={{
                cursor: "pointer",
                background: declaration ? "#f1f6fb" : "#f8f9fa",
                borderColor: declaration ? "#0d6efd" : "#dee2e6",
              }}
              onClick={() => setDeclaration((d) => !d)}
            >
              <div className="d-flex align-items-start">
                <input
                  type="checkbox"
                  className="form-check-input mt-1 me-2 flex-shrink-0"
                  id="noDuesDeclaration"
                  checked={declaration}
                  onChange={(e) => setDeclaration(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ float: "none", margin: 0, marginTop: 2 }}
                />
                <label
                  className="form-check-label text-dark ms-1"
                  htmlFor="noDuesDeclaration"
                  style={{ cursor: "pointer", fontSize: 14 }}
                >
                  I hereby declare that I have returned all items issued to me and
                  there is nothing outstanding against my name.
                </label>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action Bar */}
      <div
        className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg py-3 px-4 d-flex align-items-center justify-content-end gap-2"
        style={{ zIndex: 1000 }}
      >
        <button
          className="btn btn-outline-secondary px-4 py-2"
          onClick={() => navigate(-1)}
          disabled={submitting}
        >
          <i className="ti ti-x me-1" />
          Cancel
        </button>
        <button
          className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="ti ti-circle-check" />
          )}
          <span>{submitting ? "Submitting..." : "Submit No-Dues"}</span>
        </button>
      </div>
    </div>
  );
};

export default NoDuesForm;
